import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, Container, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import { login, resetLoginAttempts, selectAuth } from '../../store/auth/authSlice';

const LoginForm: React.FC = () => {
    const dispatch = useAppDispatch();
    const auth = useAppSelector(selectAuth);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Auto-reset login attempts after 5 minutes
    useEffect(() => {
        if (auth.loginAttempts > 0 && auth.lastAttemptTime) {
            const resetTimer = setTimeout(
                () => {
                    dispatch(resetLoginAttempts());
                },
                5 * 60 * 1000
            ); // 5 minutes

            return () => clearTimeout(resetTimer);
        }
    }, [auth.loginAttempts, auth.lastAttemptTime, dispatch]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username.trim() || !password.trim()) {
            return;
        }

        setIsSubmitting(true);

        // Add small delay to prevent brute force attempts
        await new Promise(resolve => setTimeout(resolve, 500));

        dispatch(login({ username: username.trim(), password: password.trim() }));
        setIsSubmitting(false);

        // Clear fields if login failed
        if (!auth.isAuthenticated) {
            setPassword('');
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUsername(e.target.value);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const isBlocked = auth.loginAttempts >= 5;
    const timeUntilReset = auth.lastAttemptTime ? Math.max(0, 5 * 60 * 1000 - (Date.now() - auth.lastAttemptTime)) : 0;
    const minutesLeft = Math.ceil(timeUntilReset / (60 * 1000));

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    p: 2,
                }}>
                <Card
                    sx={{
                        width: '100%',
                        maxWidth: 420,
                        boxShadow: 3,
                        borderRadius: 3,
                    }}>
                    <CardContent sx={{ p: 5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                mb: 4,
                            }}>
                            <Box
                                sx={{
                                    backgroundColor: 'primary.main',
                                    borderRadius: '50%',
                                    p: 1.5,
                                    mb: 2,
                                }}>
                                <LockOutlined sx={{ fontSize: 32, color: 'white' }} />
                            </Box>
                            <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
                                Macread
                            </Typography>
                            <Typography variant="body1" color="text.secondary" textAlign="center" mt={1}>
                                Enter username and password to access the application
                            </Typography>
                        </Box>

                        {auth.loginAttempts > 0 && !isBlocked && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Incorrect username or password. Attempts: {auth.loginAttempts}/5
                            </Alert>
                        )}

                        {isBlocked && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Too many failed attempts. Please wait {minutesLeft} minute{minutesLeft !== 1 ? 's' : ''} before trying again.
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1, textAlign: 'center', fontWeight: 500, color: 'text.secondary' }}>
                                    Username
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="text"
                                    variant="outlined"
                                    value={username}
                                    onChange={handleUsernameChange}
                                    disabled={isSubmitting || isBlocked}
                                    autoComplete="username"
                                    placeholder="Enter your username"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderRadius: 2,
                                            },
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ mb: 1, textAlign: 'center', fontWeight: 500, color: 'text.secondary' }}>
                                    Password
                                </Typography>
                                <TextField
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    variant="outlined"
                                    value={password}
                                    onChange={handlePasswordChange}
                                    disabled={isSubmitting || isBlocked}
                                    autoComplete="current-password"
                                    placeholder="Enter your password"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderRadius: 2,
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={togglePasswordVisibility} edge="end" disabled={isSubmitting || isBlocked}>
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={!username.trim() || !password.trim() || isSubmitting || isBlocked}
                                sx={{
                                    py: 1.8,
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    fontSize: '1rem',
                                    mt: 1,
                                }}>
                                {isSubmitting ? 'Verifying...' : 'Access Application'}
                            </Button>
                        </Box>

                        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={4} sx={{ fontStyle: 'italic' }}>
                            This session will remain authenticated until the browser is closed
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
};

export default LoginForm;
